"use client"

import { useEffect, useState } from 'react'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { LogoOptions, LogoResult, LogoState } from '@/types/logo'
import { logger } from "./logger"

// Define maximum storage size (5MB)
const MAX_STORAGE_SIZE = 5 * 1024 * 1024

// Check if we're in a browser environment
const isBrowser = typeof window !== 'undefined'

// Create a custom storage object that handles browser/server environments
const customStorage = {
  getItem: (name: string) => {
    if (!isBrowser) return null
    try {
      const value = localStorage.getItem(name)
      return value ? JSON.parse(value) : null
    } catch (error) {
      logger.error('Error reading from localStorage:', error)
      return null
    }
  },
  setItem: (name: string, value: any) => {
    if (!isBrowser) return
    try {
      const stringified = JSON.stringify(value)
      const currentSize = new Blob([stringified]).size
      if (currentSize > MAX_STORAGE_SIZE) {
        logger.warn('Storage quota exceeded, clearing old data')
        localStorage.clear()
      }
      localStorage.setItem(name, stringified)
    } catch (error) {
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        logger.warn('Storage quota exceeded, clearing old data')
      } else {
        logger.error('Error writing to localStorage:', error)
      }
    }
  },
  removeItem: (name: string) => {
    if (!isBrowser) return
    try {
      localStorage.removeItem(name)
    } catch (error) {
      logger.error('Error removing from localStorage:', error)
    }
  },
}

const emptyState = {
  logoOptions: {
    vibe: "",
    style: "",
    type: "",
    colorType: "",
    colorPalette: [] as string[],
    imageModel: undefined,
  } as LogoOptions,
  businessInfo: {
    prompt: "",
    name: "",
    isPrivate: false,
  },
  selectedLogo: null,
  generationStatus: "idle" as const,
  generatedLogos: [],
  result: null,
  error: null,
}

export const useLogoState = create<LogoState>()(
  persist(
    (set) => ({
      // Initial state. `isLoaded` starts false: the browser's saved draft has
      // not been read yet on the server or on the first client render.
      ...emptyState,
      isLoaded: false,

      // Actions
      setLogoOptions: (options: Partial<LogoOptions>) =>
        set((state) => ({
          logoOptions: { ...state.logoOptions, ...options },
        })),

      updateBusinessInfo: (info) =>
        set((state) => ({
          businessInfo: { ...state.businessInfo, ...info },
        })),

      setSelectedLogo: (logo: string | null) =>
        set(() => ({
          selectedLogo: logo,
        })),

      setGenerationStatus: (status: "idle" | "generating" | "success" | "error") =>
        set(() => ({
          generationStatus: status,
        })),

      setIsLoaded: (loaded: boolean) =>
        set(() => ({
          isLoaded: loaded,
        })),

      setGeneratedLogos: (logos) =>
        set(() => ({
          generatedLogos: logos,
        })),

      setResult: (result: LogoResult | null) =>
        set(() => ({
          result,
        })),

      setError: (error) =>
        set(() => ({
          error,
        })),

      reset: () =>
        set(() => ({
          ...emptyState,
          isLoaded: true,
        })),
    }),
    {
      name: "logo-storage",
      storage: customStorage,
      // Hydration is skipped on the server and performed once on the client by
      // useHydrateLogoState below, so the first render matches the server.
      skipHydration: true,
      partialize: (state) => ({
        logoOptions: state.logoOptions,
        businessInfo: state.businessInfo,
        selectedLogo: state.selectedLogo,
        result: state.result,
      }) as unknown as LogoState,
    }
  )
)

/** Fingerprints the inputs, so an unchanged brief reuses its existing result. */
export function buildResultKey(state: Pick<LogoState, 'businessInfo' | 'logoOptions'>): string {
  const { businessInfo, logoOptions } = state
  return JSON.stringify([
    businessInfo.prompt,
    businessInfo.name,
    businessInfo.isPrivate ?? false,
    logoOptions.type,
    logoOptions.vibe,
    logoOptions.colorType,
    logoOptions.colorPalette,
    logoOptions.imageModel,
  ])
}

/**
 * Reads the saved draft back out of the browser, once, after mount. Without
 * this the store is write-only and a refresh silently loses the user's work.
 * Called from _app so every page benefits.
 */
export function useHydrateLogoState(): boolean {
  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {
    let cancelled = false

    const run = async () => {
      try {
        await useLogoState.persist.rehydrate()
      } catch (error) {
        logger.error('Error restoring saved progress:', error)
      } finally {
        if (!cancelled) {
          useLogoState.setState({ isLoaded: true })
          setHydrated(true)
        }
      }
    }

    run()

    return () => {
      cancelled = true
    }
  }, [])

  return hydrated
}
